import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { ClaudeCodeOs, ClaudeCodeSetupOut } from '../types';
import { CopyButton } from './CopyButton';

interface ClaudeCodeSetupProps {
  keyId: string;
  /** Only known in the one-time reveal; when given, an "AI Agent" tab shows the raw config. */
  plaintext?: string;
}

const OS_TABS: { os: ClaudeCodeOs; label: string; terminal: string }[] = [
  { os: 'macos', label: 'macOS', terminal: 'Terminal' },
  { os: 'linux', label: 'Linux / WSL', terminal: 'a terminal' },
  { os: 'windows', label: 'Windows', terminal: 'PowerShell' },
];

/** Best guess at the viewer's OS, so the right script is showing before anyone clicks. */
export function detectOs(userAgent: string = navigator.userAgent): ClaudeCodeOs {
  if (/Windows|Win64|Win32/i.test(userAgent)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macos';
  if (/Linux|X11|CrOS/i.test(userAgent)) return 'linux';
  return 'macos';
}

/**
 * One copy-paste command that points Claude Code at the gateway with this key, per OS.
 * The script prompts for the key, so it is safe to show here and fetch again at any time;
 * it never touches the user's own settings.json (see services/claude_code_setup.py).
 */
export function ClaudeCodeSetup({ keyId, plaintext }: ClaudeCodeSetupProps) {
  const [os, setOs] = useState<ClaudeCodeOs>(() => detectOs());
  const [agentTab, setAgentTab] = useState(false);
  const [setup, setSetup] = useState<ClaudeCodeSetupOut | 'error' | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    setSetup(null);
    api
      .claudeCodeSetup(keyId, os)
      .then((res) => {
        if (gen === generation.current) setSetup(res);
      })
      .catch(() => {
        if (gen === generation.current) setSetup('error');
      });
    return () => {
      generation.current++;
    };
  }, [keyId, os]);

  const terminal = OS_TABS.find((t) => t.os === os)?.terminal ?? 'a terminal';

  return (
    <section className="claude-code-setup" aria-labelledby="cc-setup-heading">
      <h3 id="cc-setup-heading">Set up Claude Code</h3>
      <div className="admin-tabs" role="tablist" aria-label="Setup">
        {OS_TABS.map((t) => {
          const active = !agentTab && t.os === os;
          return (
            <button
              key={t.os}
              type="button"
              role="tab"
              aria-selected={active}
              className={`admin-tab ${active ? 'admin-tab-active' : ''}`}
              onClick={() => {
                setAgentTab(false);
                setOs(t.os);
              }}
            >
              {t.label}
            </button>
          );
        })}
        {plaintext ? (
          <button
            type="button"
            role="tab"
            aria-selected={agentTab}
            className={`admin-tab ${agentTab ? 'admin-tab-active' : ''}`}
            onClick={() => setAgentTab(true)}
          >
            AI Agent
          </button>
        ) : null}
      </div>

      {agentTab && plaintext ? (
        <>
          <p className="small">Configure your AI agent with this endpoint and key.</p>
          <div className="form-group">
            <label>URL</label>
            {/* Same endpoint Claude Code uses (claude_code_base_url), so the two can't drift. */}
            {setup === null ? (
              <p className="muted" role="status">
                Loading URL...
              </p>
            ) : setup === 'error' ? (
              <p className="form-note warn">Could not load the URL. Try again in a moment.</p>
            ) : (
              <div className="plaintext-box">
                <code className="plaintext-value">{setup.base_url}</code>
                <CopyButton text={setup.base_url} label="Copy URL" />
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Key</label>
            <div className="plaintext-box">
              <code className="plaintext-value">{plaintext}</code>
              <CopyButton text={plaintext} label="Copy key" />
            </div>
          </div>
        </>
      ) : setup === null ? (
        <p className="muted" role="status">
          Loading setup...
        </p>
      ) : setup === 'error' ? (
        <p className="form-note warn">Could not load the setup script. Try again in a moment.</p>
      ) : (
        <>
          <ol className="setup-steps">
            <li>
              Open {terminal}, paste this and press Enter.
              <div className="setup-script">
                <pre>
                  <code>{setup.setup_script}</code>
                </pre>
                <CopyButton text={setup.setup_script} label="Copy setup script" />
              </div>
            </li>
            <li>Paste your key when it asks. It is stored in your OS credential store, not in a file Claude Code reads.</li>
            <li>
              Start Claude Code with <code className="mono">{setup.launcher}</code>. Plain{' '}
              <code className="mono">claude</code> keeps your current setup.
            </li>
          </ol>

          {setup.warnings.map((w) => (
            <div key={w} className="callout callout-warning">
              {w}
            </div>
          ))}

          <p className="small muted">
            Models: opus <span className="mono">{setup.models.opus}</span>, sonnet{' '}
            <span className="mono">{setup.models.sonnet}</span>, haiku{' '}
            <span className="mono">{setup.models.haiku}</span>
            {setup.model_source === 'guardrail' && setup.guardrail_name
              ? ` (allowed by the ${setup.guardrail_name} guardrail)`
              : ' (deployment defaults)'}
            .
          </p>

          <details className="setup-details">
            <summary>Not working? Run the doctor</summary>
            <p className="small">
              Checks the key, the overlay and IT-managed settings, then sends one short test
              request through {setup.launcher}.
            </p>
            <div className="setup-script">
              <pre>
                <code>{setup.doctor_script}</code>
              </pre>
              <CopyButton text={setup.doctor_script} label="Copy doctor script" />
            </div>
          </details>

          <details className="setup-details">
            <summary>What the script writes</summary>
            <p className="small">
              <span className="mono">~/.claude/aigw.json</span>, layered over your own settings
              with <span className="mono">claude --settings</span>:
            </p>
            <div className="setup-script">
              <pre>
                <code>{JSON.stringify(setup.settings, null, 2)}</code>
              </pre>
              <CopyButton text={JSON.stringify(setup.settings, null, 2)} label="Copy overlay" />
            </div>
          </details>
        </>
      )}
    </section>
  );
}
